import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, getDocs, doc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Customer, CustomerRelationshipLog } from '../types/customer';
import { ClockIcon, UserIcon, DocumentArrowDownIcon, MagnifyingGlassIcon, PencilIcon, PlusIcon, FunnelIcon, ExclamationCircleIcon, CheckCircleIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, UserGroupIcon, ArchiveBoxIcon, TrashIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import CustomerRelationshipLogForm from '../components/customers/CustomerRelationshipLogForm';
import CreateRelationshipLogModal from '../components/customers/CreateRelationshipLogModal';
import EditRelationshipLogModal from '../components/customers/EditRelationshipLogModal';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import { getUserDisplayNameSync } from '../utils/userUtils';

interface LogEntryWithCustomer extends CustomerRelationshipLog {
  customerName: string;
  customerType: string;
  customerTaxNumber?: string;
}

// Kapcsolattípus ikonok és színek
const getContactTypeDisplay = (contactType: string) => {
  switch (contactType) {
    case 'phone':
      return { 
        icon: PhoneIcon, 
        label: 'Telefon', 
        color: 'text-blue-600 bg-blue-50',
        borderColor: 'border-blue-500'
      };
    case 'email':
      return { 
        icon: EnvelopeIcon, 
        label: 'Email', 
        color: 'text-green-600 bg-green-50',
        borderColor: 'border-green-500'
      };
    case 'distributor':
      return { 
        icon: BuildingOfficeIcon, 
        label: 'Elosztói', 
        color: 'text-orange-600 bg-orange-50',
        borderColor: 'border-orange-500'
      };
    case 'internal':
      return { 
        icon: UserGroupIcon, 
        label: 'Belső', 
        color: 'text-purple-600 bg-purple-50',
        borderColor: 'border-purple-500'
      };
    case 'postal':
      return { 
        icon: ArchiveBoxIcon, 
        label: 'Postai', 
        color: 'text-gray-600 bg-gray-50',
        borderColor: 'border-gray-500'
      };
    default:
      return { 
        icon: PhoneIcon, 
        label: 'Telefon', 
        color: 'text-blue-600 bg-blue-50',
        borderColor: 'border-blue-500'
      };
  }
};

const CustomerRelationshipLogs: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogEntryWithCustomer[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntryWithCustomer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [filterOpenOnly, setFilterOpenOnly] = useState(false);
  const [contactTypeFilter, setContactTypeFilter] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingLog, setEditingLog] = useState<LogEntryWithCustomer | null>(null);
  const [customersMap, setCustomersMap] = useState<Map<string, Customer>>(new Map());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<LogEntryWithCustomer | null>(null);

  // Calculate pagination
  const totalItems = filteredLogs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, filterOpenOnly, contactTypeFilter]);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const allLogs: LogEntryWithCustomer[] = [];
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          if (customerData.relationshipLogs && customerData.relationshipLogs.length > 0) {
            const customerName = customerData.type === 'organization'
              ? customerData.companyName
              : `${customerData.firstName} ${customerData.lastName}`;
              
            customerData.relationshipLogs.forEach(log => {
              allLogs.push({
                ...log,
                customerName,
                customerType: customerData.type,
                customerTaxNumber: customerData.taxNumber
              });
            });
          }
        });
        
        // Sort logs by creation date (newest first)
        const sortedLogs = allLogs.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
          const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
          return dateB.getTime() - dateA.getTime();
        });
        
        setLogs(sortedLogs);
        setFilteredLogs(sortedLogs);
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, []);

  useEffect(() => {
    // Apply filters when searchTerm, dateFilter, filterOpenOnly, or contactTypeFilter changes
    let filtered = [...logs];
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.customerName.toLowerCase().includes(term) || 
        log.content.toLowerCase().includes(term) || 
        log.entryId.toLowerCase().includes(term) ||
        (log.customerTaxNumber && log.customerTaxNumber.toLowerCase().includes(term))
      );
    }
    
    if (dateFilter) {
      filtered = filtered.filter(log => {
        const logDate = log.createdAt instanceof Date 
          ? log.createdAt 
          : log.createdAt.toDate();
          
        return logDate.toISOString().substring(0, 10) === dateFilter;
      });
    }
    
    // Apply the open tasks filter
    if (filterOpenOnly) {
      filtered = filtered.filter(log => log.isOpen);
    }
    
    // Apply contact type filter
    if (contactTypeFilter.length > 0) {
      filtered = filtered.filter(log => contactTypeFilter.includes(log.contactType || 'phone'));
    }
    
    setFilteredLogs(filtered);
  }, [logs, searchTerm, dateFilter, filterOpenOnly, contactTypeFilter]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const customersMapTemp = new Map<string, Customer>();
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          customersMapTemp.set(doc.id, customerData);
        });
        
        setCustomersMap(customersMapTemp);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    
    fetchCustomers();
  }, []);

  // Refresh customers map whenever logs are refreshed
  useEffect(() => {
    const updateCustomersMap = async () => {
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const customersMapTemp = new Map<string, Customer>();
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          customersMapTemp.set(doc.id, customerData);
        });
        
        setCustomersMap(customersMapTemp);
      } catch (error) {
        console.error('Error updating customers map:', error);
      }
    };
    
    if (!loading) {
      updateCustomersMap();
    }
  }, [logs, loading]);

  const refreshLogs = async () => {
    setLoading(true);
    try {
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const allLogs: LogEntryWithCustomer[] = [];
      
      customersSnapshot.forEach((doc) => {
        const customerData = doc.data() as Customer;
        if (customerData.relationshipLogs && customerData.relationshipLogs.length > 0) {
          const customerName = customerData.type === 'organization'
            ? customerData.companyName
            : `${customerData.firstName} ${customerData.lastName}`;
            
          customerData.relationshipLogs.forEach(log => {
            allLogs.push({
              ...log,
              customerName,
              customerType: customerData.type,
              customerTaxNumber: customerData.taxNumber
            });
          });
        }
      });
      
      // Sort logs by creation date (newest first)
      const sortedLogs = allLogs.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
        const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
        return dateB.getTime() - dateA.getTime();
      });
      
      setLogs(sortedLogs);
      setFilteredLogs(sortedLogs);
    } catch (error) {
      console.error('Error refreshing logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Prepare data for export
    const exportData = filteredLogs.map(log => {
      const createdAt = log.createdAt instanceof Date 
        ? log.createdAt 
        : log.createdAt.toDate();
        
      const contactTypeDisplay = getContactTypeDisplay(log.contactType || 'phone');
        
      return {
        'Entry ID': log.entryId,
        'Customer': log.customerName,
        'Type': log.customerType === 'organization' ? 'Organization' : 'Individual',
        'Tax Number': log.customerTaxNumber || 'N/A',
        'Contact Type': contactTypeDisplay.label,
        'Content': log.content,
        'Status': log.isOpen ? 'Open' : 'Closed',
        'Created At': createdAt.toLocaleString('hu-HU'),
        'Created By': log.createdByName || getUserDisplayNameSync(log.createdBy, user)
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RelationshipLogs');
    
    // Export the file
    XLSX.writeFile(workbook, 'CustomerRelationshipLogs.xlsx');
  };

  // Function to handle deleting a log entry
  const handleDeleteLog = async (log: LogEntryWithCustomer) => {
    if (!user) {
      toast.error('Bejelentkezés szükséges a törléshez');
      return;
    }

    // Super admin törölhet mindent, más csak a saját bejegyzését
    const canDelete = user.role === 'super_admin' || log.createdBy === user.uid;
    
    if (!canDelete) {
      toast.error('Csak a bejegyzés létrehozója vagy a rendszergazda törölheti');
      return;
    }

    setIsDeleteModalOpen(true);
    setLogToDelete(log);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <CommonPageHeader
        title="Kapcsolattartási napló"
        subtitle="Minden ügyfélkapcsolat egy helyen - Keresés, szűrés és kezelés egyszerűen"
        icon={ClockIcon}
        showLastUpdate={false}
      >
        <button
          onClick={exportToExcel}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">Excel export</span>
          <span className="sm:hidden">Excel</span>
        </button>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">Új bejegyzés</span>
          <span className="sm:hidden">Új</span>
        </button>
      </CommonPageHeader>
      
      <div className="mb-16"></div>
      
      {/* Edit Log Modal */}
      {editingLog && (
        <EditRelationshipLogModal
          isOpen={true}
          onClose={() => setEditingLog(null)}
          onSuccess={async () => {
            await refreshLogs();
            setEditingLog(null); // Modal bezárása sikeres szerkesztés után
          }}
          logToEdit={editingLog}
          customer={customersMap.get(editingLog.customerId) || { id: editingLog.customerId } as Customer}
        />
      )}
      
      {/* Filter Toggle Buttons */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="space-y-4">
          {/* Search and Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Keresés</label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ügyfél, tartalom, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dátum</label>
            <input
              type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>
          </div>

          {/* Contact Type and Status Filters */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Contact Type Filter Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kapcsolat típusa</label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setContactTypeFilter([])}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs whitespace-nowrap transition-colors
                    ${contactTypeFilter.length === 0
                      ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  <span className="mr-1">⚡</span>
                  Összes
                </button>
                {[
                  { value: 'phone', label: 'Telefon', icon: '📞' },
                  { value: 'email', label: 'Email', icon: '📧' },
                  { value: 'distributor', label: 'Elosztói', icon: '🏢' },
                  { value: 'internal', label: 'Belső', icon: '👥' },
                  { value: 'postal', label: 'Postai', icon: '📮' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      if (contactTypeFilter.includes(type.value)) {
                        setContactTypeFilter(contactTypeFilter.filter(t => t !== type.value));
                      } else {
                        setContactTypeFilter([...contactTypeFilter, type.value]);
                      }
                    }}
                    className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs whitespace-nowrap transition-colors
                      ${contactTypeFilter.includes(type.value)
                        ? type.value === 'phone'
                          ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                          : type.value === 'email'
                            ? 'border-green-500 bg-green-500/20 text-green-600'
                            : type.value === 'distributor'
                              ? 'border-orange-500 bg-orange-500/20 text-orange-600'
                              : type.value === 'internal'
                                ? 'border-purple-500 bg-purple-500/20 text-purple-600'
                                : 'border-gray-500 bg-gray-500/20 text-gray-600'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <span className="mr-1">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter Buttons */}
          <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Állapot</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilterOpenOnly(false)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs whitespace-nowrap transition-colors
                    ${!filterOpenOnly
                      ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  <span className="mr-1">✨</span>
                  Összes
                </button>
            <button
                  onClick={() => setFilterOpenOnly(true)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs whitespace-nowrap transition-colors
                    ${filterOpenOnly
                      ? 'border-amber-500 bg-amber-500/20 text-amber-600'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
            >
                  <span className="mr-1">⚠️</span>
                  Nyitott ({filteredLogs.filter(log => log.isOpen).length})
            </button>
              </div>
            </div>
          </div>

          {/* Clear All Filters */}
          {(searchTerm || dateFilter || filterOpenOnly || contactTypeFilter.length > 0) && (
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setFilterOpenOnly(false);
                  setContactTypeFilter([]);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md hover:bg-gray-100 transition-colors"
              >
                ✕ Összes szűrő törlése
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Modern Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {/* Total Entries */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs font-medium">Összes bejegyzés</p>
              <p className="text-xl font-bold mt-0.5">
                {filteredLogs.length}
                {filteredLogs.length !== logs.length && (
                  <span className="text-sm text-blue-200 ml-1">/ {logs.length}</span>
                )}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-2">
              <DocumentArrowDownIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          {filteredLogs.length !== logs.length && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-blue-100 mb-1">
                <span>Szűrt</span>
                <span>{Math.round((filteredLogs.length / logs.length) * 100)}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div 
                  className="bg-white rounded-full h-1.5 transition-all duration-300" 
                  style={{ width: `${(filteredLogs.length / logs.length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Open Entries */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-xs font-medium">Nyitott ügyek</p>
              <p className="text-xl font-bold mt-0.5">
                {filteredLogs.filter(log => log.isOpen).length}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-2">
              <ExclamationCircleIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-xs text-amber-100 mb-1">
              <span>Prioritás</span>
              <span>
                {filteredLogs.length > 0 
                  ? Math.round((filteredLogs.filter(log => log.isOpen).length / filteredLogs.length) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div 
                className="bg-white rounded-full h-1.5 transition-all duration-300" 
                style={{ 
                  width: filteredLogs.length > 0 
                    ? `${(filteredLogs.filter(log => log.isOpen).length / filteredLogs.length) * 100}%` 
                    : '0%' 
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Closed Entries */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs font-medium">Lezárt ügyek</p>
              <p className="text-xl font-bold mt-0.5">
                {filteredLogs.filter(log => !log.isOpen).length}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-2">
              <CheckCircleIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-xs text-green-100 mb-1">
              <span>Teljesítve</span>
              <span>
                {filteredLogs.length > 0 
                  ? Math.round((filteredLogs.filter(log => !log.isOpen).length / filteredLogs.length) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div 
                className="bg-white rounded-full h-1.5 transition-all duration-300" 
                style={{ 
                  width: filteredLogs.length > 0 
                    ? `${(filteredLogs.filter(log => !log.isOpen).length / filteredLogs.length) * 100}%` 
                    : '0%' 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Logs list */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">{t('common.loading') || 'Loading...'}</p>
        </div>
      ) : (
        <>
          {/* Modern Log Cards */}
          <div className="space-y-4">
            {currentLogs.length === 0 ? (
              <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DocumentArrowDownIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm || dateFilter || filterOpenOnly || contactTypeFilter.length > 0
                      ? 'Nincs találat'
                      : 'Még nincsenek bejegyzések'}
                  </h3>
          <p className="text-gray-500">
                    {searchTerm || dateFilter || filterOpenOnly || contactTypeFilter.length > 0
                      ? 'Próbáljon meg más szűrési feltételeket használni'
                      : 'Kezdje el az első kapcsolattartási napló bejegyzés létrehozását'}
          </p>
                </div>
        </div>
      ) : (
              currentLogs.map((log) => {
            const createdAt = log.createdAt instanceof Date 
              ? log.createdAt 
              : log.createdAt.toDate();
              
            const formattedDate = new Intl.DateTimeFormat('hu-HU', {
                  year: '2-digit',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(createdAt);

            const isCurrentUserCreator = user && log.createdBy === user.uid;
                const canDeleteLog = user && (user.role === 'super_admin' || log.createdBy === user.uid);
                const contactTypeDisplay = getContactTypeDisplay(log.contactType || 'phone');
                const ContactTypeIcon = contactTypeDisplay.icon;

            return (
                  <div 
                    key={log.id} 
                    className={`group relative bg-white rounded-xl border-l-4 ${contactTypeDisplay.borderColor} border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 hover:border-gray-300`}
                  >
                    {/* Subtle gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    
                    <div className="relative p-5">
                      {/* Header with enhanced badges and actions */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Entry ID with enhanced styling */}
                          <span className="inline-flex items-center text-xs font-mono text-gray-600 bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                            #{log.entryId.split('_').pop() || log.entryId.substring(-6)}
                          </span>
                          
                          {/* Contact Type Badge with enhanced styling */}
                          <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${contactTypeDisplay.color} ring-1 ring-black/5`}>
                            <ContactTypeIcon className="h-3.5 w-3.5 mr-1.5" />
                            {contactTypeDisplay.label}
                          </div>
                          
                          {/* Status Badge with enhanced styling */}
                          <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ring-1 ring-black/5 ${
                  log.isOpen 
                              ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800' 
                              : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800'
                          }`}>
                            {log.isOpen ? '⚡ Aktív' : '✨ Befejezve'}
                    </div>
                  </div>
                        
                        {/* Enhanced Actions */}
                        {(isCurrentUserCreator || canDeleteLog) && (
                          <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                            {isCurrentUserCreator && (
                              <button
                                onClick={() => setEditingLog(log)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-md"
                                title="Szerkesztés"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDeleteLog && (
                              <button
                                onClick={() => handleDeleteLog(log)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-md"
                                title="Törlés"
                                disabled={isSubmitting}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Enhanced Customer Info */}
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-gray-800 transition-colors">
                          {log.customerName} 
                          <span className="text-sm font-medium text-gray-500 ml-2">
                            {log.customerType === 'organization' ? '🏢 Szervezet' : '👤 Magánszemély'}
                          </span>
                        </h3>
                      {log.customerTaxNumber && (
                          <p className="text-sm text-gray-600 flex items-center">
                            <span className="inline-block w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                            Adószám: <span className="font-mono ml-1">{log.customerTaxNumber}</span>
                          </p>
                        )}
                        </div>
                      
                      {/* Enhanced Content */}
                      <div className="text-sm text-gray-800 mb-4 whitespace-pre-wrap bg-gradient-to-br from-gray-50 to-gray-100/50 p-4 rounded-lg border border-gray-200/50 leading-relaxed">
                        {log.content}
                      </div>
                      
                      {/* Enhanced Footer with better visual hierarchy */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center text-xs text-gray-500">
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
                            <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">{formattedDate}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></div>
                            <UserIcon className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">{log.createdByName || getUserDisplayNameSync(log.createdBy, user)}</span>
                          </div>
                        </div>
                        
                        {/* Priority indicator for open items */}
                        {log.isOpen && (
                          <div className="inline-flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-1.5 animate-pulse"></div>
                            Figyelmet igényel
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 bg-white rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                {/* Items per page selector */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">Oldalanként:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Page info */}
                <div className="text-sm text-gray-700">
                  {startIndex + 1}-{Math.min(endIndex, totalItems)} / {totalItems} bejegyzés
                </div>

                {/* Pagination buttons */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    «
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded ${
                          currentPage === pageNum
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    »
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    »»
                  </button>
                </div>
              </div>
        </div>
          )}
        </>
      )}
      
      {/* Create Relationship Log Modal */}
      <CreateRelationshipLogModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={refreshLogs}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && logToDelete && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={async () => {
            setIsSubmitting(true);
            try {
              const customer = customersMap.get(logToDelete.customerId);
              if (!customer) {
                throw new Error('Customer not found');
              }

              // Find the log to remove
              const logToRemove = customer.relationshipLogs?.find(l => l.id === logToDelete.id);
              if (!logToRemove) {
                throw new Error('Log entry not found');
              }

              // Remove the log entry from Firebase
              const customerRef = doc(db, 'customers', logToDelete.customerId);
              await updateDoc(customerRef, {
                relationshipLogs: arrayRemove(logToRemove)
              });

              toast.success(t('customers.relationshipLog.deleteSuccess') || 'Bejegyzés sikeresen törölve');
              await refreshLogs();
            } catch (error) {
              console.error('Failed to delete log entry:', error);
              toast.error(t('customers.relationshipLog.deleteError') || 'Nem sikerült törölni a bejegyzést');
            } finally {
              setIsDeleteModalOpen(false);
              setLogToDelete(null);
              setIsSubmitting(false);
            }
          }}
          title="Bejegyzés törlése"
          deleteMessage="Biztosan törölni szeretné ezt a kapcsolattartási bejegyzést?"
          deleteWarning="Ez a művelet nem vonható vissza. A bejegyzés véglegesen törlődik."
        />
      )}
    </div>
  );
};

export default CustomerRelationshipLogs; 